import React, { useState, useEffect } from 'react';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import { ADGMBreadcrumbs, ADGMImage } from 'aurora-components';

import { keyEmptyCheck, getPropertType } from 'aurora-utils/utils';
import isEmpty from 'lodash/isEmpty';
import messages from 'aurora-translations';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';

import classNames from 'classnames';
import RejectReturnAlertBox from '../RejectReturnAlertBox';
import styles from './styles';

/**
 * Return Flow Layout.
 *
 * @param {object} application - applicaion object.
 * @param {object} error - error object.
 * @param {boolean} showApplicationHeader - boolean to show application header, if false, property header will be shown.
 * @param {Array} buttonsConfig - array of buttons config. Each button config should have title, onClick and isPrimary.
 * @param {object} property - property object.
 */

function ReturnFlowLayout({
  classes,
  error,
  application,
  returnReasons,
  showApplicationHeader,
  buttonsConfig,
  property,
  children,
}) {
  const locale = localStorage.getItem('lang');

  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    if (error?.statusCode || error?.message || error) setShowSnackbar(true);
  }, [error]);

  const { applicationType, applicationNumber } = application?.applicationDetails || {};

  const breadCrumbs = [
    {
      text: keyEmptyCheck('home'),
      to: '/app',
    },
    { text: keyEmptyCheck('tasks'), to: '/app/applications', search: 'page=1&type=tasks' },

    {
      text: keyEmptyCheck(applicationType),
    },
  ];

  return (
    <Container className={classes.container}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => {
            setShowSnackbar(false);
          }}
          severity='error'
          variant='filled'
          sx={{ width: '100%' }}
        >
          {messages[error?.message]?.id ? keyEmptyCheck(error?.message) : error?.message}
        </Alert>
      </Snackbar>

      <Grid container spacing={4} alignItems='center'>
        <Grid item xs={12}>
          <ADGMBreadcrumbs paths={breadCrumbs} />
        </Grid>

        {/* Application Type and Number */}
        {showApplicationHeader && (
          <Grid item xs={12} md={9}>
            <Typography variant='h6' component='h1' className={classes.Title}>
              {keyEmptyCheck(applicationType)}
            </Typography>
            <Typography variant='body1' className={classes.subTitle}>
              {keyEmptyCheck('Application_ID')}
              {': '}
              {applicationNumber}
            </Typography>
          </Grid>
        )}

        {/* Property Information */}
        {!showApplicationHeader && (
          <Grid item xs={12} md={9} className={classes.propertyHeaderContainer}>
            <ADGMImage alt='Image2' name='offPlan1x.svg' />
            <div>
              <Typography variant='h6' component='h1' className={classes.Title}>
                {keyEmptyCheck('plot_number')}:{property?.plotNumber}
                <Chip
                  label={keyEmptyCheck(getPropertType(property?.propertyType))}
                  className={classNames(classes.chip, classes.darkgreen)}
                />
              </Typography>
              <Typography variant='body1' className={classes.subTitle}>
                {locale === 'en'
                  ? property?.districtEn + ',' + property?.communityEn
                  : property?.districtAr + ', ' + property?.communityAr}
                {!isEmpty(property?.mortgagesUpdated) && (
                  <Chip label={keyEmptyCheck('mortgaged')} className={classNames(classes.chip, classes.warning)} />
                )}
              </Typography>
            </div>
          </Grid>
        )}

        {/* Buttons */}
        <Grid item container xs spacing={1}>
          {buttonsConfig?.map(({ title, onClick, isPrimary }) => (
            <Grid item xs={12}>
              <Button
                onClick={onClick}
                className={classNames(classes.btn, isPrimary ? classes.primaryButton : classes.secondaryButton)}
              >
                {keyEmptyCheck(title)}
              </Button>
            </Grid>
          ))}
        </Grid>

        {!isEmpty(returnReasons) && (
          <Grid item xs={12}>
            <RejectReturnAlertBox
              isReject={false}
              reasons={Array.isArray(returnReasons) ? returnReasons : [returnReasons]}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          {children}
        </Grid>
      </Grid>
    </Container>
  );
}

ReturnFlowLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  returnReasons: PropTypes.array,
  error: PropTypes.object,
  application: PropTypes.object.isRequired,
  showApplicationHeader: PropTypes.bool,
  buttonsConfig: PropTypes.array,
  property: PropTypes.object,
  children: PropTypes.node.isRequired,
};

ReturnFlowLayout.defaultProps = {
  showApplicationHeader: true,
  returnReasons: [],
  error: null,
  buttonsConfig: [],
  property: {},
};

export default withStyles(styles)(ReturnFlowLayout);
